import { useCallback, useEffect, useState } from "react";
import { fetchRefundHistory } from "./paymentsApi";

/**
 * Normalizes the response into an array.
 * @param {*} data - Decrypted API payload
 * @returns {Array}
 */
function normalizeHistory(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.attempts)) return data.attempts;
    if (Array.isArray(data?.history)) return data.history;
    return [];
}

/**
 * Inner async loader for refund attempts.
 * @param {Object} args - Loader arguments
 */
async function loadHistory({ paymentId, signal, setAttempts, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchRefundHistory(paymentId);
        if (!signal.aborted) setAttempts(normalizeHistory(data));
    } catch (err) {
        if (!signal.aborted) setError(err?.response?.data || "Impossible de charger l'historique");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook fetching the refund attempts history for a payment.
 * Triggers a fetch whenever paymentId changes or refresh() is called.
 * @param {number|string|null} paymentId - Target payment ID
 * @returns {{ attempts: Array, loading: boolean, error: any, refresh: Function }}
 */
function useRefundsHistory(paymentId) {
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!paymentId) { setAttempts([]); return undefined; }
        const controller = new AbortController();
        loadHistory({ paymentId, signal: controller.signal, setAttempts, setError, setLoading });
        return () => controller.abort();
    }, [paymentId, tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);

    return { attempts, loading, error, refresh };
}

export default useRefundsHistory;
